import { useState, useEffect, useMemo } from 'react';
import { useMeeting } from '../meeting/meetingProviderContextDef';
import { Stream } from '@videosdk.live/js-sdk';

/**
 * Hook to get a Stream by ID and access its methods.
 * @param {string} streamId
 * @param {{
 *  onStreamStateChanged?: Function,
 * }} options
 * @returns {{
 * kind: 'audio' | 'video' | 'share' | 'shareAudio'
 * stream: Stream;
 * codec: string;
 * track: MediaStreamTrack;
 * paused: boolean;
 * pause: () => void,
 * resume: () => void,
 * }}
 */
const useStream = (streamId, { onStreamStateChanged = () => {} } = {}) => {
  const [stream, setStream] = useState(null);

  const meeting = useMeeting();

  const { participants } = useMemo(() => {
    const participants = meeting?.participants;
    return {
      participants
    };
  }, [meeting]);

  const _handleStreamStateChanged = data => {
    onStreamStateChanged(data);
  };

  useEffect(() => {
    if (!meeting) return;
    if (!streamId) return;

    // Iterate over all participants' streams to find the stream
    const findStream = () => {
      for (let participant of participants.values()) {
        const stream = participant.streams.get(streamId);
        if (stream != null) {
          return stream;
        }
      }
      return null;
    };

    const s = findStream();
    if (!s) return;
    setStream(s);

    s?.on('state', _handleStreamStateChanged);

    return () => {
      s?.off('state', _handleStreamStateChanged);
    };
  }, [streamId]);

  const pause = () => {
    if (stream) {
      stream.pause();
    }
  };

  const resume = () => {
    if (stream) {
      stream.resume();
    }
  };

  return {
    kind: stream?.kind,
    codec: stream?.codec,
    track: stream?.track,
    paused: stream?.paused,
    stream,
    pause,
    resume
  };
};

export default useStream;
